import { isEqual, isNull } from 'lodash';
import { useCallback, useEffect, useState } from 'react';

import { useMessage } from 'components/ui';

// Long enough to notice the flash, short enough that the row does not stay highlighted
// while the user keeps reordering.
const MOVED_ROW_HIGHLIGHT_MS = 1200;

export function usePriority({ data, onSavePriority }) {
  const message = useMessage();
  const [isPrioritizing, setIsPrioritizing] = useState(false);
  const [prioritizedItems, setPrioritizedItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [movedItem, setMovedItem] = useState(null);

  /**
   * Callers may wire this straight to a button's `onClick`, so anything other than an explicitly
   * provided array falls back to the full data set.
   */
  const handleOpenPriority = useCallback(
    (displayedItems) => {
      setPrioritizedItems(Array.isArray(displayedItems) ? displayedItems : data);
      setIsPrioritizing(true);
    },
    [data]
  );

  const handleCancelPriority = useCallback(() => {
    setPrioritizedItems(null);
    setIsPrioritizing(false);
    setMovedItem(null);
  }, []);

  const handleDragUpdate = useCallback((orderedItems, movedItemKey) => {
    setPrioritizedItems(orderedItems);
    if (movedItemKey !== undefined) {
      setMovedItem((previous) => ({
        key: movedItemKey,
        // Bumping the count restarts the highlight when the same row is moved again.
        moveCount: (previous?.moveCount ?? 0) + 1,
      }));
    }
  }, []);

  useEffect(() => {
    if (!movedItem) {
      return undefined;
    }

    const timeout = setTimeout(() => setMovedItem(null), MOVED_ROW_HIGHLIGHT_MS);
    return () => clearTimeout(timeout);
  }, [movedItem]);

  const handleSavePriority = useCallback(async () => {
    setLoading(true);

    try {
      await onSavePriority(prioritizedItems);
    } catch (err) {
      message.error(err.message);
    } finally {
      setLoading(false);
      setIsPrioritizing(false);
      setPrioritizedItems(null);
      setMovedItem(null);
    }
  }, [message, onSavePriority, prioritizedItems]);

  const allItemsHavePriority =
    prioritizedItems?.length &&
    data?.length &&
    prioritizedItems.map((i) => i.priority).filter(Boolean).length === data.length;
  /**
   * Disable saving priority if:
   * - priority is loading
   * OR
   *   - prioritizedItems are NOT null
   *   AND
   *   - prioritizedItems equals the value of data
   *   AND
   *   - all prioritizedItems have a priority value already set
   */
  const disableSavePriority =
    loading ||
    (!isNull(prioritizedItems) && isEqual(prioritizedItems, data) && allItemsHavePriority);

  return {
    handleOpenPriority,
    handleCancelPriority,
    handleDragUpdate,
    handleSavePriority,
    isPrioritizing,
    loading,
    disableSavePriority,
    movedItem,
    prioritizedItems,
  };
}
